import {MyCheckBuilderState, RulesetReportConfig} from '../../my-checks-builder/my-checks-builder.model';

/** Zuordnung alte → neue ID beim Duplizieren eines BIC-Prüflaufs samt Prüfregeln. */
export interface RulesetIdMapping {
  sets: Record<string, string>;
  /** Nur gefüllt, wo die Quelle die Regeln mitliefert (KI-Sets). */
  rules: Record<string, string>;
}

/**
 * Ordnet die Regeln einer Kopie ihren Originalen zu – über den Namen, weil die Kopie serverseitig
 * entsteht und die Reihenfolge nicht garantiert ist. Jede Kopie wird nur einmal verbraucht, damit
 * gleichnamige Regeln nicht kollidieren.
 */
export function buildRuleIdMap(
  originalRules: { id?: string; name?: string }[],
  copiedRules: { id?: string; name?: string }[],
): Record<string, string> {
  const map: Record<string, string> = {};
  const remaining = [...(copiedRules ?? [])];

  (originalRules ?? []).forEach(original => {
    if (!original?.id) return;
    let matchIndex = remaining.findIndex(copy => !!copy?.id && copy.name === original.name);
    // Kein Namenstreffer: die erste noch freie Kopie. Über den Index zu gehen wäre falsch, weil
    // `remaining` bei jedem Treffer schrumpft.
    if (matchIndex < 0) matchIndex = remaining.findIndex(copy => !!copy?.id);
    if (matchIndex < 0) return;
    const [copy] = remaining.splice(matchIndex, 1);
    if (copy?.id) map[original.id] = copy.id;
  });

  return map;
}

/**
 * Biegt alle Regelset-Bezüge eines Prüflauf-Zustands auf die Kopien um.
 *
 * <p>Arbeitet auf dem Builder-State, nicht am Manifest: dort stecken die Regelset-IDs an einem
 * halben Dutzend Stellen, der State führt sie an einer – und `buildManifestFromState` schreibt
 * daraus wieder alle konsistent. Nicht gemappte IDs bleiben stehen, damit eine Teilauswahl die
 * BIC-Originale behält.
 */
export function remapRulesetIds(
  state: MyCheckBuilderState,
  mapping: RulesetIdMapping,
): MyCheckBuilderState {
  const mapSetId = (id: string): string => mapping.sets[id] ?? id;

  const mapRuleId = (id: string): string => {
    const direct = mapping.rules[id];
    if (direct) return direct;
    // IDS: die Spezifikations-ID trägt die Set-ID als Präfix.
    for (const [oldSetId, newSetId] of Object.entries(mapping.sets)) {
      const prefix = `${oldSetId}::`;
      if (id.startsWith(prefix)) return `${newSetId}::${id.slice(prefix.length)}`;
    }
    return id;
  };

  const selectedRulesetTypes: MyCheckBuilderState['selectedRulesetTypes'] = {};
  for (const [id, type] of Object.entries(state.selectedRulesetTypes ?? {})) {
    selectedRulesetTypes[mapSetId(id)] = type;
  }

  const reportConfigs: RulesetReportConfig[] = (state.reportConfigs ?? []).map(config => ({
    ...config,
    rulesetId: mapSetId(config.rulesetId),
  }));

  return {
    ...state,
    selectedRuleSetIds: (state.selectedRuleSetIds ?? []).map(mapSetId),
    selectedRuleIds: (state.selectedRuleIds ?? []).map(mapRuleId),
    partiallySelectedRuleSetIds: (state.partiallySelectedRuleSetIds ?? []).map(mapSetId),
    selectedRulesetTypes,
    reportConfigs,
  };
}
